import React from 'react';
import { getRem, transitionAnimation, typographyBody1, typographyBody2 } from 'lib/core';
import { removeObjectProperties } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { BORDER_POSITIONS, INPUT_SIZES } from '../constants';

const BaseInputFieldStyles = css`
  ${transitionAnimation('border, box-shadow')}
  ${({ theme }) => typographyBody1(theme)}
  background: transparent;
  border: none;
  box-shadow: none;
  box-sizing: border-box;
  min-width: 0;
  outline: none;
  padding: 0;
  padding-right: ${({ theme }) => theme.size.spacing.small.value};
  width: 100%;

  ${({ $size, theme }) =>
    $size === INPUT_SIZES.LARGE
      ? css`
          ${typographyBody2(theme)};
          height: ${getRem(24)};
        `
      : css`
          height: ${getRem(20)};
        `}

  &::placeholder {
    color: ${({ theme }) => theme.color.gray[600].value};
    font-weight: ${({ theme }) => theme.font.weight.light.value};
  }

  &::-ms-clear,
  &::-ms-reveal {
    display: none;
  }
  /* Direct selection necessary to apply styling for all other possible password input cases */
  &[type='password']:not([value='']) {
    font-family: ${({ theme }) => theme.font.family.obscured.value};
  }
  ${({ $isObscured, theme }) =>
    $isObscured &&
    css`
      font-family: ${theme.font.family.obscured.value};
    `}
`;

const StyledBaseInputField = styled.input`
  ${BaseInputFieldStyles}
`;

const BaseInputField = React.forwardRef(({ hasError, hideBorder, isObscured, size, ...other }, ref) => {
  const filteredOtherProps = removeObjectProperties(other, 'textAlign');
  return (
    <StyledBaseInputField
      $hasError={hasError}
      $hideBorder={hideBorder}
      $isObscured={isObscured}
      $size={size}
      {...filteredOtherProps}
      ref={ref}
    />
  );
});

BaseInputField.propTypes = {
  /** If true, add error styling */
  hasError: PropTypes.bool,
  /** Defines which side of input container border should be hidden */
  hideBorder: PropTypes.oneOf(Object.values(BORDER_POSITIONS)),
  /** If true, changes password mask dots to be bigger */
  isObscured: PropTypes.bool,
  /** Set the size of the input */
  size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
};

export { BaseInputField, BaseInputFieldStyles, StyledBaseInputField };
